import { useContext } from 'react'

import {
  Columns, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useOrderApproval from './hooks/useOrderApproval'

import Disclaimers from './components/Disclaimers'
import OrderDetails from './components/OrderDetails'
import OrderItem from './components/OrderItem'
import Provider from './components/Provider'

const MODAL_KEY = 'ApproveOrderModal'

type ModalCallbacks = {
  approveOrder: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ApproveOrderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { approveOrder, closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { order: initOrder = {} } = modalPayload

  const orderApprovalPayload = useOrderApproval(initOrder)
  const {
    approveOrderParams,
    callbacks: {
      approveOrder: approveFn,
    },
    errors,
    filteredOrderItems,
    order,
    provider,
  } = orderApprovalPayload

  return (
    <SidebarModal
      callbacks={callbacks}
      disableAnimation
      disableOverlay
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="fullscreen"
    >
      <SidebarModal.Header callbacks={callbacks} title="Confirm" titleSecondLine="Order" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={false}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            {/*
             // @ts-expect-error TS2740 */}
            <Provider order={order} provider={provider} />

            {filteredOrderItems.map(orderItem => (
              <OrderItem
                errors={errors[orderItem.id] || []}
                key={orderItem.id}
                orderItem={orderItem}
                orderApprovalPayload={orderApprovalPayload}
              />
            ))}

            <Disclaimers orderApprovalPayload={orderApprovalPayload} />
          </Columns.Main>

          <Columns.Sidebar>
            <OrderDetails
              callbacks={{
                approveOrder: () => approveOrder({
                  callbacks: {
                    action: approveFn,
                    afterAction: closeModal,
                  },
                  entityParams: approveOrderParams,
                  requestOptions: {
                    include_order_items: true,
                  },
                  toastText: 'Order Approved',
                }),
              }}
              orderApprovalPayload={orderApprovalPayload}
            />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ApproveOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
